import React, { useCallback, useEffect, useState } from "react";

import { Loader2, Mail, Send } from "lucide-react";

import Button from "../../../Components/Button";
import Input from "../../../Components/Input";
import ToggleSwitch from "../../../Components/ToggleSwitch";
import ToggleWeeklyDigestModal, {
  WeeklyDigestToggleScope,
} from "../../../Container/AdminOrganizationContainer/ToggleWeeklyDigestModal";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchWeeklyDigestConfig,
  fetchWeeklyDigestSponsors,
  sendTestWeeklyDigest,
  updateWeeklyDigestConfig,
  updateWeeklyDigestSponsors,
} from "../../../Store/Reducers/VendorAdminSlice";
import { toast } from "../../../Utils/Helper/toast";

const WEEKLY_DIGEST_KEY = "weekly_digest";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NOT_ENTITLED_MSG =
  "Weekly Digest is not enabled for this organization. Contact your Izba administrator.";

type PendingDigestToggle = {
  scope: WeeklyDigestToggleScope;
  newValue: boolean;
};

const getErrorMessage = (error: unknown, fallback: string): string =>
  typeof error === "string" && error.trim() ? error : fallback;

const WeeklyDigestOrgSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    weeklyDigestValue,
    weeklyDigestLoading,
    weeklyDigestError,
    weeklyDigestUpdateLoading,
    weeklyDigestUpdateError,
    weeklyDigestStatus,
    weeklyDigestStatusLoading,
  } = useAppSelector(state => state.vendorAdmin);

  const orgNotEntitled = weeklyDigestStatus?.org_entitled === false;
  const digestStatusReady =
    weeklyDigestStatus != null && !weeklyDigestStatusLoading;

  const [enabled, setEnabled] = useState<boolean>(true);
  const [allSponsorsEnabled, setAllSponsorsEnabled] = useState<boolean>(false);
  const [sponsorsStatusLoading, setSponsorsStatusLoading] =
    useState<boolean>(false);
  const [sponsorsUpdateLoading, setSponsorsUpdateLoading] =
    useState<boolean>(false);
  const [sponsorCount, setSponsorCount] = useState<number>(0);
  const [sponsorEmail, setSponsorEmail] = useState<string>("");
  const [recipientEmail, setRecipientEmail] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const [pendingToggle, setPendingToggle] =
    useState<PendingDigestToggle | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const toggleChecked =
    weeklyDigestStatus != null
      ? weeklyDigestStatus.org_entitled && weeklyDigestStatus.org_active
      : orgNotEntitled
        ? false
        : enabled;

  const loadSponsorsStatus = useCallback(async () => {
    if (!digestStatusReady) {
      return;
    }
    if (orgNotEntitled) {
      setAllSponsorsEnabled(false);
      setSponsorCount(0);
      return;
    }
    setSponsorsStatusLoading(true);
    try {
      const payload = await dispatch(fetchWeeklyDigestSponsors()).unwrap();
      setAllSponsorsEnabled(Boolean(payload.all_sponsors_enabled));
      setSponsorCount(Number(payload.sponsor_count) || 0);
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Failed to load sponsor digest settings."
      );
      if (message.toLowerCase().includes("not enabled for this organization")) {
        setAllSponsorsEnabled(false);
        setSponsorCount(0);
        return;
      }
      toast.error(message);
    } finally {
      setSponsorsStatusLoading(false);
    }
  }, [digestStatusReady, orgNotEntitled, dispatch]);

  const load = useCallback(() => {
    dispatch(fetchWeeklyDigestConfig());
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    void loadSponsorsStatus();
  }, [loadSponsorsStatus]);

  useEffect(() => {
    if (weeklyDigestValue) {
      setEnabled(Boolean(weeklyDigestValue.enabled));
    }
  }, [weeklyDigestValue]);

  useEffect(() => {
    if (weeklyDigestError) {
      toast.error(weeklyDigestError);
    }
  }, [weeklyDigestError]);

  useEffect(() => {
    if (weeklyDigestUpdateError) {
      toast.error(weeklyDigestUpdateError);
    }
  }, [weeklyDigestUpdateError]);

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setPendingToggle(null);
  };

  const requestOrgToggle = (newValue: boolean) => {
    if (newValue && orgNotEntitled) {
      toast.error(NOT_ENTITLED_MSG);
      return;
    }
    setPendingToggle({ scope: "organization", newValue });
    setConfirmModalOpen(true);
  };

  const requestAllSponsorsToggle = (newValue: boolean) => {
    if (orgNotEntitled) {
      toast.error(NOT_ENTITLED_MSG);
      return;
    }
    setPendingToggle({ scope: "all_sponsors", newValue });
    setConfirmModalOpen(true);
  };

  const applyOrgToggle = async (newValue: boolean) => {
    setEnabled(newValue);
    try {
      await dispatch(
        updateWeeklyDigestConfig({
          key: WEEKLY_DIGEST_KEY,
          value: { enabled: newValue },
        })
      ).unwrap();
      toast.success("Weekly digest settings saved.");
    } catch (error) {
      setEnabled(!newValue);
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to save weekly digest settings."
      );
      throw error;
    }
  };

  const applyAllSponsorsToggle = async (newValue: boolean) => {
    const previous = allSponsorsEnabled;
    setAllSponsorsEnabled(newValue);
    setSponsorsUpdateLoading(true);
    try {
      const payload = await dispatch(
        updateWeeklyDigestSponsors({ enabled: newValue })
      ).unwrap();
      setAllSponsorsEnabled(Boolean(payload.all_sponsors_enabled ?? newValue));
      setSponsorCount(Number(payload.sponsor_count) || 0);
      toast.success(
        newValue
          ? "Weekly digest enabled for all active sponsors."
          : "Weekly digest disabled for all active sponsors."
      );
    } catch (error) {
      setAllSponsorsEnabled(previous);
      toast.error(
        getErrorMessage(error, "Failed to update sponsor digest settings.")
      );
      throw error;
    } finally {
      setSponsorsUpdateLoading(false);
    }
  };

  const handleConfirmToggle = async () => {
    if (!pendingToggle) return;
    const { scope, newValue } = pendingToggle;
    try {
      if (scope === "organization") {
        await applyOrgToggle(newValue);
      } else {
        await applyAllSponsorsToggle(newValue);
      }
      closeConfirmModal();
    } catch {
      // Errors already toasted; keep modal open so user can cancel or retry.
    }
  };

  const handleSendTest = async () => {
    if (orgNotEntitled) {
      toast.error(NOT_ENTITLED_MSG);
      return;
    }
    const sponsor = sponsorEmail.trim();
    const recipient = recipientEmail.trim();
    if (!EMAIL_RE.test(sponsor)) {
      toast.error("Please enter a valid sponsor email address.");
      return;
    }
    if (!EMAIL_RE.test(recipient)) {
      toast.error("Please enter a valid recipient email address.");
      return;
    }
    setSending(true);
    try {
      await dispatch(
        sendTestWeeklyDigest({
          sponsor_email: sponsor,
          recipient_email: recipient,
        })
      ).unwrap();
      toast.success(`Weekly digest for ${sponsor} sent to ${recipient}.`);
      setSponsorEmail("");
      setRecipientEmail("");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to send the weekly digest."));
    } finally {
      setSending(false);
    }
  };

  const sponsorsHelper =
    sponsorCount > 0
      ? `Applies to all ${sponsorCount} executive sponsor${
          sponsorCount === 1 ? "" : "s"
        } in this organization.`
      : "Applies to all executive sponsors in this organization.";

  const confirmLoading =
    pendingToggle?.scope === "organization"
      ? weeklyDigestUpdateLoading
      : sponsorsUpdateLoading;

  return (
    <div className="settings-section rounded-lg border border-gray-200 bg-white shadow">
      <div className="p-6">
        <div className="mb-1 flex items-center gap-2 border-b border-gray-200 pb-4">
          <Mail className="h-5 w-5 text-violet-600" />
          <h2 className="fs-18 fw-600 mb-0 text-gray-900">
            Weekly digest (Monday Morning Briefing)
          </h2>
        </div>
        <p className="mb-5 mt-4 text-sm leading-relaxed text-gray-600">
          Send a personalized Monday morning briefing email to your executive
          sponsors summarizing their portfolio, level mix, recent activity, and
          a tip of the week.
        </p>
        {orgNotEntitled && (
          <p className="mb-5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Weekly Digest has been disabled for your organization by Izba Admin.
          </p>
        )}
        {weeklyDigestLoading ? (
          <div className="flex h-10 items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
            Loading…
          </div>
        ) : (
          <div className="space-y-0">
            <div className="flex items-start justify-between gap-6 py-4">
              <div className="min-w-0 flex-1 pr-2">
                <label
                  htmlFor="weekly-digest-enabled"
                  className="mb-1 block cursor-pointer text-base font-semibold text-gray-900"
                >
                  Organization emails
                </label>
                <p className="mb-0 text-sm leading-relaxed text-gray-600">
                  Enable weekly digest emails for this organization. When off,
                  no Monday Morning Briefing emails are sent.
                </p>
              </div>
              <div className="shrink-0 pt-0.5">
                <ToggleSwitch
                  id="weekly-digest-enabled"
                  checked={toggleChecked}
                  disabled={weeklyDigestUpdateLoading || confirmModalOpen}
                  onChange={requestOrgToggle}
                />
              </div>
            </div>

            <div className="border-t border-gray-100" />

            <div className="flex items-start justify-between gap-6 py-4">
              <div className="min-w-0 flex-1 pr-2">
                <label
                  htmlFor="weekly-digest-all-sponsors"
                  className="mb-1 block cursor-pointer text-base font-semibold text-gray-900"
                >
                  All sponsors
                </label>
                <p className="mb-0 text-sm leading-relaxed text-gray-600">
                  Enable weekly digest for all sponsors. {sponsorsHelper}
                </p>
              </div>
              <div className="shrink-0 pt-0.5">
                <ToggleSwitch
                  id="weekly-digest-all-sponsors"
                  checked={allSponsorsEnabled}
                  disabled={
                    orgNotEntitled ||
                    sponsorsStatusLoading ||
                    sponsorsUpdateLoading ||
                    confirmModalOpen
                  }
                  onChange={requestAllSponsorsToggle}
                />
              </div>
            </div>

            <div className="border-t border-gray-200 pt-5">
              <h3 className="mb-1 text-base font-semibold text-gray-900">
                Send Weekly Digest
              </h3>
              <p className="mb-3 text-sm leading-relaxed text-gray-600">
                Generate a sponsor&apos;s Monday Morning Briefing and send it to
                any recipient. The sponsor must exist, be eligible for Weekly
                Digest emails, and have at least one assigned account.
              </p>
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input
                    variant="field"
                    label="Sponsor Email"
                    name="sponsor-digest-email"
                    type="email"
                    placeholder="sponsor@company.com"
                    value={sponsorEmail}
                    onChange={e => setSponsorEmail(e.target.value)}
                    className="mb-0"
                  />
                  <Input
                    variant="field"
                    label="Recipient Email"
                    name="recipient-digest-email"
                    type="email"
                    placeholder="recipient@company.com"
                    value={recipientEmail}
                    onChange={e => setRecipientEmail(e.target.value)}
                    className="mb-0"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    onClick={handleSendTest}
                    disabled={
                      sending || !sponsorEmail.trim() || !recipientEmail.trim()
                    }
                    loading={sending}
                    icon={<Send className="h-4 w-4" />}
                  >
                    Send Email
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ToggleWeeklyDigestModal
        isOpen={confirmModalOpen}
        toggle={closeConfirmModal}
        organizationName="this organization"
        isEnabling={pendingToggle?.newValue ?? false}
        scope={pendingToggle?.scope ?? "organization"}
        onConfirm={handleConfirmToggle}
        loading={confirmLoading}
      />
    </div>
  );
};

export default WeeklyDigestOrgSettings;
